import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { SendHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppDispatch } from "@/hooks/useReduxHooks";
import { chatApi, useSendChatMessageMutation } from "@/services/(my-space)/api.chat";
import { cn } from "@/utils/cn";
import { getErrorMessage } from "@/utils/error.utils";
import { newClientMessageId } from "../_lib/chat-utils";

/**
 * A one-line text reply to a chat without opening it (the message popup, the
 * top-bar inbox). Enter sends, Shift+Enter breaks the line, Esc gives up; a
 * failure keeps the draft. Sending moves my read pointer on the server too.
 */
export const QuickReplyBox = ({
  conversationId,
  name,
  onSent,
  onCancel,
  focusKey,
  className,
}: {
  conversationId: number;
  /** Who it goes to: the placeholder and the "sent" toast. */
  name: string;
  onSent: () => void;
  onCancel: () => void;
  /** Re-focus the box when this changes (e.g. a newer message arrived). */
  focusKey?: unknown;
  className?: string;
}) => {
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const [sendMessage, { isLoading: sending }] = useSendChatMessageMutation();
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [focusKey]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    try {
      await sendMessage({
        conversationId,
        clientMessageId: newClientMessageId(),
        message: { type: "text", body },
      }).unwrap();
      dispatch(chatApi.util.invalidateTags(["Unread"]));
      toast.success(t("popup.reply_sent", { name }));
      setDraft("");
      onSent();
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.send")));
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    } else if (e.key === "Escape") {
      // Closes the box only, not the popover or dialog around it.
      e.preventDefault();
      e.stopPropagation();
      setDraft("");
      onCancel();
    }
  };

  return (
    <div className={cn("flex items-end gap-2", className)}>
      <textarea
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        rows={1}
        maxLength={4000}
        dir="auto"
        disabled={sending}
        placeholder={t("popup.reply_placeholder", { name })}
        aria-label={t("popup.reply_placeholder", { name })}
        className="max-h-24 min-h-8 flex-1 resize-none rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] leading-snug outline-none field-sizing-content placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/30"
      />
      <Button
        variant="primary"
        size="icon-xs"
        onClick={() => void send()}
        isLoading={sending}
        disabled={!draft.trim()}
        aria-label={t("popup.send_reply")}
        className="size-8 shrink-0"
      >
        <SendHorizontal className="size-4 rtl:-scale-x-100" />
      </Button>
    </div>
  );
};
